import React, { useState, useEffect } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { Link } from "../components/L";
import axios from "axios";
import { Calendar, Euro, ArrowRight, Clock } from "lucide-react";
import useSEO from "../hooks/useSEO";
import { formatPrice } from "../utils/price";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Subscribe Form
const SubscribeForm = () => {
  const { tx } = useLanguage();
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [agreed, setAgreed] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !agreed) return;
    setStatus("sending");
    try {
      const names = form.name.trim().split(" ", 2);
      await axios.post(`${API}/leads`, {
        first_name: names[0] || "",
        last_name: names[1] || "",
        email: form.email.trim(),
        phone: form.phone.trim(),
        referral_source: "Subscribe Form",
        referral_detail: "Eventet Page Subscribe",
      });
      setStatus("success");
      setForm({ name: "", phone: "", email: "" });
    } catch {
      setStatus("success");
      setForm({ name: "", phone: "", email: "" });
    }
  };

  if (status === "success") {
    return (
      <div className="bg-white/[0.06] backdrop-blur-md border border-white/10 rounded-2xl p-8 text-center" data-testid="subscribe-success">
        <div className="w-14 h-14 rounded-full bg-[#08707A]/20 flex items-center justify-center mx-auto mb-4">
          <i className="fas fa-check text-[#3CD4E1] text-xl"></i>
        </div>
        <h3 className="text-lg font-bold text-white mb-2">{tx("Faleminderit!")}</h3>
        <p className="text-white/60 text-sm">{tx("Regjistrimi juaj u krye me sukses. Do te njoftoheni per eventet e reja.")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white/[0.06] backdrop-blur-md border border-white/10 rounded-2xl p-8" data-testid="subscribe-form">
      <h3 className="text-lg font-bold text-white mb-1 font-['Inter_Tight']">{tx("Regjistrohu Tani")}</h3>
      <p className="text-white/50 text-xs mb-6">{tx("Vendos te dhenat e tua dhe behu pjese e eventeve tona.")}</p>
      <div className="space-y-3.5">
        <div className="relative">
          <i className="fas fa-user absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-xs"></i>
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={tx("Emri Mbiemri")} required className="w-full rounded-lg bg-white/[0.07] border border-white/10 pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#3CD4E1]/50 focus:ring-1 focus:ring-[#3CD4E1]/30 transition-colors" data-testid="subscribe-name" />
        </div>
        <div className="relative">
          <i className="fas fa-phone absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-xs"></i>
          <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={tx("Nr. Celular")} className="w-full rounded-lg bg-white/[0.07] border border-white/10 pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#3CD4E1]/50 focus:ring-1 focus:ring-[#3CD4E1]/30 transition-colors" data-testid="subscribe-phone" />
        </div>
        <div className="relative">
          <i className="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-xs"></i>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" required className="w-full rounded-lg bg-white/[0.07] border border-white/10 pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#3CD4E1]/50 focus:ring-1 focus:ring-[#3CD4E1]/30 transition-colors" data-testid="subscribe-email" />
        </div>
      </div>
      <label className="flex items-start gap-3 mt-5 cursor-pointer select-none" data-testid="subscribe-terms">
        <div className="relative mt-0.5 shrink-0">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="sr-only peer" />
          <div className="w-[18px] h-[18px] rounded border border-white/20 bg-white/[0.05] peer-checked:bg-[#08707A] peer-checked:border-[#3CD4E1] transition-colors flex items-center justify-center">
            {agreed && <i className="fas fa-check text-white text-[9px]"></i>}
          </div>
        </div>
        <span className="text-white/60 text-[11px] leading-relaxed">
          {tx("Une pranoj")} <a href="/kushtet" className="text-[#3CD4E1] hover:underline">{tx("Termat dhe Kushtet")}</a> {tx("dhe")} <a href="/privatesia" className="text-[#3CD4E1] hover:underline">{tx("Politika e Privatesise")}</a> {tx("ne Albanian Aligners Academy")}
        </span>
      </label>
      <button type="submit" disabled={status === "sending" || !agreed} className="mt-5 w-full rounded-lg py-3.5 text-sm font-bold text-white inline-flex items-center justify-center gap-3 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0" style={{ background: "linear-gradient(130deg, #3CD4E1 0%, #08707A 100%)" }} data-testid="subscribe-submit">
        {status === "sending" ? (
          <span className="inline-flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> {tx("Duke derguar...")}</span>
        ) : (
          <>{tx("Regjistrohu")} <i className="fas fa-long-arrow-alt-right text-base"></i></>
        )}
      </button>
    </form>
  );
};

// Countdown component
const EventCountdown = ({ event }) => {
  const { tx } = useLanguage();
  const [timeLeft, setTimeLeft] = useState({ dite: 0, ore: 0, min: 0, sek: 0 });

  useEffect(() => {
    if (!event) return;
    const parts = event.date.split("/");
    const target = new Date(`${parts[2]}-${parts[1]}-${parts[0]}T09:00:00`);

    const tick = () => {
      const now = new Date();
      const diff = Math.max(0, target - now);
      setTimeLeft({
        dite: Math.floor(diff / (1000 * 60 * 60 * 24)),
        ore: Math.floor((diff / (1000 * 60 * 60)) % 24),
        min: Math.floor((diff / (1000 * 60)) % 60),
        sek: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [event]);

  if (!event) return null;

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-6 md:p-8" data-testid="event-countdown">
      <div className="flex items-center gap-2 mb-3">
        <Clock size={16} className="text-[#3CD4E1]" />
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#3CD4E1]">{tx("Eventi i Rradhes")}</span>
      </div>
      <h3 className="text-white font-bold text-lg mb-2 leading-snug" style={{ fontFamily: "'Noto Sans', sans-serif" }}>
        {event.title}
      </h3>
      <p className="text-white/70 text-sm mb-1"><i className="fas fa-user mr-2"></i>{event.speaker}</p>
      <p className="text-white/70 text-sm mb-5"><i className="fas fa-calendar-alt mr-2"></i>{event.date}</p>

      <div className="grid grid-cols-4 gap-3">
        {[
          { val: timeLeft.dite, label: tx("Dite") },
          { val: timeLeft.ore, label: tx("Ore") },
          { val: timeLeft.min, label: tx("Min") },
          { val: timeLeft.sek, label: tx("Sek") },
        ].map((t) => (
          <div key={t.label} className="text-center bg-white/10 rounded-lg py-3">
            <p className="text-2xl md:text-3xl font-black text-white" style={{ fontFamily: "'Noto Sans', sans-serif" }}>
              {String(t.val).padStart(2, "0")}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-white/60 mt-1">{t.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center justify-between">
        <span className="text-white font-bold text-lg">{event.price ? formatPrice(event.price) : tx("Falas")}</span>
      </div>
    </div>
  );
};

const EventsPage = () => {
  const { tx } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: tx("Evente - Kurse dhe Trajnime Live per Dentiste"),
    description: tx("Kalendari i eventeve live te Albanian Aligners Academy - workshop-e, masterclass, konferenca dhe trajnime per dentiste me eksperte nderkombetare."),
    keywords: "evente dentare, kurse per dentiste, trajnime live, workshop stomatologjik, konferenca ortodoncie, masterclass implantologji",
    canonical: "https://aligners.academy/evente",
  });

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API}/events`);
        setEvents(res.data);
      } catch (err) {
        console.error("Error fetching events:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const upcoming = events.filter((e) => e.category === "upcoming");
  const past = events.filter((e) => e.category === "past");
  const nextEvent = upcoming.length > 0 ? upcoming[0] : null;

  return (
    <div data-testid="events-page">
      {/* Hero Banner */}
      <section className="relative py-14 md:py-20 px-6 overflow-hidden" data-testid="events-hero">
        {/* Background image */}
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(/api/files/local/hero-bg.png)` }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(130deg, rgba(60,212,225,0.85) 0%, rgba(0,63,114,0.9) 100%)' }} />
        {/* Decorative circles */}
        <div className="absolute top-[-80px] right-[-80px] w-[300px] h-[300px] rounded-full bg-white/5" />
        <div className="absolute bottom-[-60px] left-[-60px] w-[200px] h-[200px] rounded-full bg-white/5" />
        <div className="absolute top-[50%] right-[30%] w-[120px] h-[120px] rounded-full bg-white/3" />

        <div className="max-w-[1350px] mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            {/* Left content */}
            <div className="lg:col-span-3">
              <h1 className="text-white mb-5" style={{ fontFamily: "'Noto Sans', sans-serif", fontWeight: 700, fontSize: '48px', lineHeight: 1.2 }} data-testid="events-title">
                {tx("Evente")}
              </h1>
              <p className="text-white text-base leading-relaxed mb-4" style={{ fontFamily: "'Noto Sans', sans-serif", fontWeight: 700 }}>
                {tx("Kalendari yne i eventeve live eshte i mbushur plot me ngjarje unike dhe edukim te gjalle profesional, ku dijet takohen me pasionin!")}
              </p>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                {tx("Nga intervista me eksperte nderkombetare, diskutime klinike interaktive, formate ne stilin TED, talk show dentare, deri te konferenca online dhe trajnime te drejtperdrejta - cdo event eshte krijuar per te frymezuar, informuar dhe permiresuar praktiken tende klinike.")}
              </p>
              <ul className="mb-4 space-y-1">
                <li className="text-white/90 text-sm"><i className="fas fa-check-circle mr-2 text-[#3CD4E1]"></i>{tx("Shiko live. Meso ndryshe. Zbulo me shume.")}</li>
              </ul>
              <p className="text-white/70 text-sm italic mb-6">
                {tx("Bashkohu me komunitetin e profesionisteve qe kerkojne me shume se thjesht nje trajnim.")}
              </p>
              <a
                href="#upcoming"
                className="rounded-full px-8 py-3 font-semibold transition-all duration-300 hover:-translate-y-1 inline-flex items-center gap-2 bg-white text-[#003F72] shadow-lg hover:bg-white/90"
                data-testid="events-scroll-btn"
              >
                {tx("Shiko Eventet")} <ArrowRight size={18} />
              </a>
            </div>

            {/* Right - Masterclass Poster */}
            <div className="lg:col-span-2">
              <Link to="/evente/masterclass" className="block group relative" data-testid="hero-poster-link">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#3CD4E1]/30 to-[#003F72]/30 blur-2xl group-hover:blur-3xl transition-all duration-700 -z-0" />
                <img
                  src="/api/files/local/masterclass-poster.png"
                  alt="Modern Implantology Masterclass 2026"
                  className="relative z-10 w-full max-w-[360px] mx-auto rounded-2xl shadow-2xl ring-1 ring-white/20 group-hover:scale-[1.03] group-hover:-rotate-1 transition-all duration-700"
                  loading="eager"
                />
                <div className="relative z-10 text-center mt-5">
                  <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/15 backdrop-blur-md text-white text-sm font-semibold border border-white/20 group-hover:bg-white group-hover:text-[#003F72] transition-all">
                    {tx("Shiko Modulet")} <i className="fas fa-arrow-right text-xs"></i>
                  </span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section id="upcoming" className="py-20 md:py-32 px-6 md:px-12 max-w-[1350px] mx-auto" data-testid="upcoming-events-section">
        <div className="mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1CA3B0] mb-4">{tx("Te ardhshme")}</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-['Inter_Tight']">
            {tx("Eventet e Ardhshme")}
          </h2>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 h-96 animate-pulse" />
            ))}
          </div>
        ) : upcoming.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
            <Calendar className="mx-auto text-[#828D9E] mb-4" size={48} />
            <p className="text-[#828D9E] text-lg">{tx("Nuk ka evente te ardhshme per momentin.")}</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Masterclass Feature Card */}
            <Link to="/evente/masterclass" className="block group" data-testid="masterclass-feature-card">
              <div className="bg-white border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-500 rounded-2xl overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] lg:grid-cols-[380px_1fr]">
                  <div className="relative overflow-hidden">
                    <img
                      src="/api/files/local/masterclass-poster-alt.png"
                      alt="Modern Implantology Masterclass"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      style={{ minHeight: '280px' }}
                    />
                    <div className="absolute top-4 left-4 bg-[#003F72] text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
                      Masterclass
                    </div>
                  </div>
                  <div className="p-6 md:p-8 flex flex-col justify-center">
                    <p className="text-[#3CD4E1] text-xs font-bold uppercase tracking-wider mb-2">{tx("6 Module | Qershor - Tetor 2026")}</p>
                    <h3 className="text-2xl font-bold tracking-tight mb-3 font-['Inter_Tight'] text-[#0A0D10] group-hover:text-[#1CA3B0] transition-colors">
                      Modern Implantology Masterclass
                    </h3>
                    <p className="text-sm italic text-[#1CA3B0] mb-3">From Fundamentals to Full-Arch Excellence</p>
                    <p className="text-sm text-[#4A5568] mb-4 leading-relaxed">{tx("Programi me i plote i implantologjise ne rajon. 6 module intensive me 9+ eksperte nderkombetare, nga bazat deri ne rehabilitimin full-arch.")}</p>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-[#828D9E] mb-4">
                      <span className="flex items-center gap-2"><Calendar size={16} /> {tx("5 Qershor - 25 Tetor 2026")}</span>
                      <span className="flex items-center gap-2"><Clock size={16} /> {tx("12 Dite Trajnimi")}</span>
                    </div>
                    <div className="inline-flex items-center gap-2 text-sm font-bold text-[#003F72] group-hover:text-[#1CA3B0] transition-colors">
                      {tx("Shiko Masterclass")} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>

            {/* Other upcoming events */}
            {upcoming.filter(e => e.id !== 'masterclass-2026').length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {upcoming.filter(e => e.id !== 'masterclass-2026').map((event, i) => (
              <Link
                to={`/evente/${event.slug || event.id}`}
                key={event.id}
                className="bg-white border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-500 rounded-2xl overflow-hidden group flex flex-col"
                data-testid={`upcoming-event-${i}`}
              >
                <div className="relative overflow-hidden aspect-[3/4] bg-[#f0f4f5]">
                  <img
                    src={event.image_url || "https://images.pexels.com/photos/23625648/pexels-photo-23625648.jpeg"}
                    alt={event.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3 bg-[#1CA3B0] text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm">
                    {tx("E ardhshme")}
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="text-base font-bold tracking-tight mb-2 font-['Inter_Tight'] text-[#0A0D10] line-clamp-2 group-hover:text-[#1CA3B0] transition-colors">
                    {event.title}
                  </h3>
                  <p className="text-xs text-[#4A5568] mb-3 leading-relaxed line-clamp-2">{event.description}</p>
                  <div className="flex items-center justify-between pt-3 mt-auto border-t border-slate-100">
                    <span className="flex items-center gap-1.5 text-xs text-[#828D9E]">
                      <Calendar size={12} /> {event.date}
                    </span>
                    <span className="text-sm font-bold text-[#08707A]">
                      {event.price ? formatPrice(event.price) : tx("Falas")}
                    </span>
                  </div>
                  <span className="mt-3 text-xs font-semibold text-[#1CA3B0] inline-flex items-center gap-1.5">
                    {tx("Detaje")} <i className="fas fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
                  </span>
                </div>
              </Link>
            ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Past Events */}
      <section className="bg-[#FAFAFA] py-20 md:py-32 px-6 md:px-12" data-testid="past-events-section">
        <div className="max-w-[1350px] mx-auto">
          <div className="mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1CA3B0] mb-4">{tx("Te kaluara")}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-['Inter_Tight']">
              {tx("Eventet e Kaluara")}
            </h2>
          </div>

          {past.length === 0 ? (
            <p className="text-[#828D9E] text-center py-12">{tx("Nuk ka evente te kaluara.")}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {past.map((event, i) => (
                <Link
                  to={`/evente/${event.slug || event.id}`}
                  key={event.id}
                  className="bg-white border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-500 rounded-2xl overflow-hidden card-hover block group"
                  data-testid={`past-event-${i}`}
                >
                  <div className="overflow-hidden aspect-[3/4] bg-[#f0f4f5]">
                    <img
                      src={event.image_url || "https://images.pexels.com/photos/23625648/pexels-photo-23625648.jpeg"}
                      alt={event.title}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-sm font-bold tracking-tight mb-2 font-['Inter_Tight'] text-[#0A0D10] line-clamp-2">
                      {event.title}
                    </h3>
                    <span className="flex items-center gap-2 text-xs text-[#828D9E]">
                      <Calendar size={12} /> {event.date}
                    </span>
                    <span className="mt-3 text-xs font-semibold text-[#1CA3B0] inline-flex items-center gap-1">
                      {tx("Shiko Detajet")} <i className="fas fa-arrow-right text-[10px]"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Bashkohu Section with Video */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-white relative overflow-hidden" data-testid="events-join-section">
        {/* Subtle background decorations */}
        <div className="absolute top-10 right-10 w-[200px] h-[200px] rounded-full opacity-5" style={{ background: 'linear-gradient(130deg, #3CD4E1, #003F72)' }} />
        <div className="absolute bottom-20 left-[-50px] w-[150px] h-[150px] rounded-full opacity-5" style={{ background: 'linear-gradient(130deg, #003F72, #3CD4E1)' }} />
        <div className="absolute top-[40%] left-[60%] w-[100px] h-[100px] rounded-full opacity-[0.03]" style={{ background: '#003F72' }} />

        <div className="max-w-[1350px] mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Noto Sans', sans-serif", color: 'rgb(28, 163, 176)' }}>
                {tx("Bashkohu ne Panele me Eksperte dhe behu pjese e Eventeve Unike")}
              </h2>
              <hr className="mb-8" style={{ borderColor: 'rgba(28, 163, 176, 0.1)' }} />
              <p className="text-[#4A5568] leading-relaxed mb-6">
                {tx("Albanian Aligners Academy po pasuron vazhdimishtri kalendarin e saj me evente te reja, permbajtje me vlere dhe mundesi te reja zhvillimi profesional per ortodoncet, dentistet dhe te gjithe profesionistet e fushes dentare.")}
              </p>
              <p className="text-[#4A5568] leading-relaxed mb-6">
                {tx("Nga workshop-e praktike te fokusuara ne temat me te avancuara te ortodoncise dixhitale dhe sistemet aligner, deri te konferencat nderkombetare virtuale dhe fizike me pjesemarrjen e disa prej emrave me te njohur ne bote, akademia ofron nje platforme te hapur per mesim, shkembim pervojash dhe ndertim komuniteti.")}
              </p>
              <p className="text-[#4A5568] leading-relaxed mb-8">
                {tx("Ne besojme se bashkepunimi profesional, mjetesimi dhe qasja ne dije te perdorueshme jane nder faktoret me te rendesishem per zhvillimin e qendrueshëm te cdo profesionisti ne stomatologji dhe ortodonci. Kjo eshte arsyeja pse cdo event qe organizojme fokusohet jo vetem ne aspektin teorik dhe teknologjik, por edhe ne ndertimin e nje kulture bashkepunimi dhe perkushtimi ndaj ekselences klinike.")}
              </p>
              <Link
                to="/trajnere"
                className="inline-flex items-center gap-2 rounded-lg px-6 py-3 font-semibold text-sm text-white transition-all duration-300 hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(130deg, #2D3748 0%, #1A202C 100%)' }}
                data-testid="events-meet-trainers-btn"
              >
                {tx("NJIHU ME TRAJNERET TANE")} <i className="fas fa-arrow-right"></i>
              </Link>
            </div>
            <div className="relative flex justify-end items-center">
              <div className="rounded-xl overflow-hidden shadow-xl w-full" style={{ borderRadius: '12px' }}>
                <video
                  controls
                  className="w-full"
                  style={{ minHeight: '450px', objectFit: 'cover' }}
                  poster="/api/files/local/hero-bg.png"
                  data-testid="events-video"
                >
                  <source src="/api/files/local/events-video.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="events-subscribe">
        <style>{`
          @keyframes bgShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}</style>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(270deg, #0A0D14, #141B2D, #1A1F35, #0F1420, #0A0D14)', backgroundSize: '400% 400%', animation: 'bgShift 20s ease infinite' }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className="relative z-10 max-w-[1350px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#3CD4E1] mb-4">{tx("Regjistrohu")}</p>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6 font-['Inter_Tight'] leading-tight">
                {tx("Qendro i Informuar per Eventet Ekskluzive te Aligners Academy")}
              </h2>
              <p className="text-white/70 leading-relaxed mb-8">
                {tx("Bashkohu me komunitetin e stomatologeve qe duan te zhvillojne ekspertizen e tyre ne ortodoncine dixhitale dhe trajtimet me aligner.")}
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#08707A]/20 flex items-center justify-center mt-0.5 shrink-0"><i className="fas fa-check text-[9px] text-[#3CD4E1]"></i></div>
                  <p className="text-white text-sm">{tx("Ftesa per workshop-e dhe konferenca")}</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#08707A]/20 flex items-center justify-center mt-0.5 shrink-0"><i className="fas fa-check text-[9px] text-[#3CD4E1]"></i></div>
                  <p className="text-white text-sm">{tx("Njoftime per evente te reja")}</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#08707A]/20 flex items-center justify-center mt-0.5 shrink-0"><i className="fas fa-check text-[9px] text-[#3CD4E1]"></i></div>
                  <p className="text-white text-sm">{tx("Akses prioritare per regjistrim")}</p>
                </div>
              </div>
            </div>
            <div>
              <SubscribeForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
